import {
  SupportTicketId as SupportTicketIdSchema,
  type SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import type {
  SupportTicketListItemResponse as SupportTicketListItemResponseType,
  UpdateSupportTicketInput,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import type { SearchAfterCursor } from "@redotech/effect-api-shared/schema/pagination";
import { BasicIndex } from "@tanstack/db";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import { createCollection } from "@tanstack/react-db";
import type { QueryClient } from "@tanstack/react-query";
import {
  buildSupportTicketListUrlParams,
  DEFAULT_SUPPORT_TICKET_PAGE_SIZE,
  SUPPORT_TICKET_PREFETCH_PAGES_AHEAD,
  toUpdateSupportTicketInput,
  type SupportTicketListFetchOptions,
  type SupportTicketListUrlParams,
} from "./support-ticket-list-requests";
import {
  supportTicketQueryScopeKey,
  supportTicketsPageQueryKey,
  type SupportTicketQueryScope,
} from "./support-ticket-query-keys";

const FIVE_MINUTES_MS = 5 * 60 * 1000;
const PAGE_CURSOR_CACHE_LIMIT = 100;

export const supportTicketListQueryKeys = {
  all: ["supportTickets", "list"] as const,
  list: (scope?: SupportTicketQueryScope) =>
    [
      ...supportTicketListQueryKeys.all,
      ...supportTicketQueryScopeKey(scope),
    ] as const,
};

export interface SupportTicketListApi {
  getSupportTicket(
    supportTicketId: SupportTicketId,
  ): Promise<SupportTicketListItemResponseType>;
  listSupportTickets(
    params: SupportTicketListUrlParams,
  ): Promise<{
    items: readonly SupportTicketListItemResponseType[];
    nextCursor?: SearchAfterCursor;
    totalCount: number;
  }>;
  updateSupportTicket(
    supportTicketId: SupportTicketId,
    payload: UpdateSupportTicketInput,
  ): Promise<void>;
}

export interface CreateSupportTicketListCollectionOptions {
  queryClient: QueryClient;
  api: SupportTicketListApi;
  scope?: SupportTicketQueryScope;
  onUpdateError?: (supportTicketId: string, error: Error) => void;
}

export interface PageLoadResult {
  items: SupportTicketListItemResponseType[];
  nextCursor?: SearchAfterCursor;
  supportTicketIds: string[];
  totalCount: number;
}

export interface SupportTicketPageMetadata {
  nextCursor?: SearchAfterCursor;
  supportTicketIds: string[];
  totalCount: number;
}

interface PrefetchPagesInput {
  filterGroup: string | undefined;
  nextCursor?: SearchAfterCursor;
  page: number;
  pageSize?: number;
  sorts?: string;
  totalCount?: number;
}

interface FetchPageOptions extends SupportTicketListFetchOptions {
  knownTotalCount?: number;
}

const pageCursorKey = (
  filterGroup: string | undefined,
  page: number,
  pageSize: number,
  sorts: string | undefined,
) => JSON.stringify([filterGroup ?? null, page, pageSize, sorts ?? null]);

function trimPageCursorCache(pageCursors: Map<string, SearchAfterCursor>) {
  while (pageCursors.size > PAGE_CURSOR_CACHE_LIMIT) {
    const oldestKey = pageCursors.keys().next().value;
    if (!oldestKey) {
      return;
    }
    pageCursors.delete(oldestKey);
  }
}

function normalizeListItemForCollection(
  item: SupportTicketListItemResponseType,
): SupportTicketListItemResponseType {
  return { ...item, searchHighlights: item.searchHighlights ?? [] };
}

function toPageMetadata({
  items,
  nextCursor,
  totalCount,
}: {
  items: readonly SupportTicketListItemResponseType[];
  nextCursor?: SearchAfterCursor;
  totalCount: number;
}): SupportTicketPageMetadata {
  return {
    ...(nextCursor ? { nextCursor } : {}),
    supportTicketIds: items.map((item) => item.id),
    totalCount,
  };
}

export function createSupportTicketListCollection({
  queryClient,
  api,
  scope,
  onUpdateError,
}: CreateSupportTicketListCollectionOptions) {
  const queryKey = supportTicketListQueryKeys.list(scope);
  const pageCursors = new Map<string, SearchAfterCursor>();
  const collection = createCollection(
    queryCollectionOptions({
      queryKey,
      syncMode: "eager",
      autoIndex: "eager",
      defaultIndexType: BasicIndex,
      queryFn: async (): Promise<SupportTicketListItemResponseType[]> =>
        queryClient.getQueryData<SupportTicketListItemResponseType[]>(
          queryKey,
        ) ?? [],
      getKey: (item) => item.id,
      queryClient,
      staleTime: FIVE_MINUTES_MS,
      onUpdate: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const supportTicketId = SupportTicketIdSchema.make(
            String(mutation.key),
          );
          const payload = toUpdateSupportTicketInput(mutation.changes);
          try {
            await api.updateSupportTicket(supportTicketId, payload);
          } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            onUpdateError?.(String(mutation.key), error);
            throw error;
          }
        }

        return { refetch: false };
      },
    }),
  );

  collection.startSyncImmediate();

  const fetchPage = async (
    page: number,
    filterGroup: string | undefined,
    pageSize?: number,
    sorts?: string,
    options: FetchPageOptions = {},
  ): Promise<PageLoadResult> => {
    const effectivePageSize = pageSize ?? DEFAULT_SUPPORT_TICKET_PAGE_SIZE;
    const { knownTotalCount, ...fetchOptions } = options;
    const searchAfter =
      fetchOptions.searchAfter ??
      pageCursors.get(
        pageCursorKey(filterGroup, page, effectivePageSize, sorts),
      );
    const result = await api.listSupportTickets(
      buildSupportTicketListUrlParams(page, filterGroup, pageSize, sorts, {
        ...fetchOptions,
        searchAfter,
      }),
    );
    const items = result.items.map(normalizeListItemForCollection);
    const pageMetadata = toPageMetadata({
      items,
      nextCursor: result.nextCursor,
      totalCount: knownTotalCount ?? result.totalCount,
    });
    const nextCursorKey = pageCursorKey(
      filterGroup,
      page + 1,
      effectivePageSize,
      sorts,
    );
    if (items.length >= effectivePageSize && result.nextCursor?.length) {
      pageCursors.set(nextCursorKey, result.nextCursor);
      trimPageCursorCache(pageCursors);
    } else {
      pageCursors.delete(nextCursorKey);
    }

    collection.utils.writeBatch(() => {
      for (const item of items) {
        collection.utils.writeUpsert(item);
      }
    });
    queryClient.setQueryData(
      supportTicketsPageQueryKey({
        encodedFilterGroup: filterGroup,
        encodedSorts: sorts,
        page,
        pageSize,
        scope,
      }),
      pageMetadata,
    );

    return { items, ...pageMetadata };
  };

  const prefetchPagesAfter = async ({
    filterGroup,
    nextCursor,
    page,
    pageSize,
    sorts,
    totalCount,
  }: PrefetchPagesInput): Promise<void> => {
    const effectivePageSize = pageSize ?? DEFAULT_SUPPORT_TICKET_PAGE_SIZE;
    let searchAfter = nextCursor;
    if (
      !searchAfter?.length ||
      (page + 1) * effectivePageSize >= (totalCount ?? 0)
    ) {
      return;
    }

    for (let i = 1; i <= SUPPORT_TICKET_PREFETCH_PAGES_AHEAD; i++) {
      try {
        const result = await fetchPage(page + i, filterGroup, pageSize, sorts, {
          includeHighlights: false,
          includeTotalCount: false,
          knownTotalCount: totalCount,
          searchAfter,
        });
        if (
          result.items.length < effectivePageSize ||
          !result.nextCursor?.length
        ) {
          return;
        }
        searchAfter = result.nextCursor;
      } catch {
        return;
      }
    }
  };

  return { collection, fetchPage, prefetchPagesAfter, queryScope: scope };
}

export type SupportTicketListCollectionWithLoader = ReturnType<
  typeof createSupportTicketListCollection
>;
